import { ThunkDispatch } from "@reduxjs/toolkit";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { panoramaActions, PanoramaBeforeImageState } from '../../reducers/panorama';
import { selectCanProcessPanorama, selectCanRegenPanorama } from '../../selectors';
import AlertModal from "../common/AlertModal";
import ConfirmModal from "../common/ConfirmModal";
import { ConfirmGenerateAfterPhotoIcon } from '../SVGIcons';
import { PanoramaMaxFileSize } from '../../constants';

// State型定義
interface RootState {
  panorama: {
    isConfirmPopupShow: boolean;
    isRegenPopupShow: boolean;
    isPhotoTooBigPopupShow: boolean;
    isAspectRatioErrorShow: boolean;
    isClearAllPopupShow: boolean;
    beforeImages: PanoramaBeforeImageState[];
    [key: string]: unknown;
  };
  currentUser: {
    creditConsumed: number;
    creditLimit: number;
    isLimitInfinity: boolean;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

// mapStateToPropsの型定義
function mapStateToProps(state: RootState) {
  const canProcess = selectCanProcessPanorama(state);
  const canRegen = selectCanRegenPanorama(state);
  // 未処理画像の数（クレジット消費数）
  const unprocessedImageCount = state.panorama.beforeImages.filter(img => !img.id).length;

  return {
    isConfirmPopupShow: state.panorama.isConfirmPopupShow,
    isRegenPopupShow: state.panorama.isRegenPopupShow,
    isPhotoTooBigPopupShow: state.panorama.isPhotoTooBigPopupShow,
    isAspectRatioErrorShow: state.panorama.isAspectRatioErrorShow,
    isClearAllPopupShow: state.panorama.isClearAllPopupShow,
    canProcess,
    canRegen,
    creditConsumed: state.currentUser.creditConsumed,
    creditLimit: state.currentUser.creditLimit,
    isLimitInfinity: state.currentUser.isLimitInfinity,
    unprocessedImageCount,
  };
}

// コンポーネントのProps型定義
interface PanoramaModalsProps {
  isConfirmPopupShow: boolean;
  isRegenPopupShow: boolean;
  isPhotoTooBigPopupShow: boolean;
  isAspectRatioErrorShow: boolean;
  isClearAllPopupShow: boolean;
  canProcess: boolean;
  canRegen: boolean;
  creditConsumed: number;
  creditLimit: number;
  isLimitInfinity: boolean;
  unprocessedImageCount: number;
}

const PanoramaModals = (props: PanoramaModalsProps) => {
  const {
    isConfirmPopupShow,
    isRegenPopupShow,
    isPhotoTooBigPopupShow,
    isAspectRatioErrorShow,
    isClearAllPopupShow,
    canProcess,
    canRegen,
    creditConsumed,
    creditLimit,
    isLimitInfinity,
    unprocessedImageCount,
  } = props;

  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const navigate = useNavigate();

  // 共通処理
  const closeConfirmPopup = () => {
    dispatch(panoramaActions.setIsConfirmPopupShow(false));
  };

  const closeRegenPopup = () => {
    dispatch(panoramaActions.setIsRegenPopupShow(false));
  };

  const closePhotoTooBigPopup = () => {
    dispatch(panoramaActions.setIsPhotoTooBigPopupShow(false));
  };

  const closeAspectRatioError = () => {
    dispatch(panoramaActions.setIsAspectRatioErrorShow(false));
  };

  const closeClearAllPopup = () => {
    dispatch(panoramaActions.setIsClearAllPopupShow(false));
  };

  // アクション処理
  const processPanorama = () => {
    if (!canProcess) return;
    dispatch(panoramaActions.setIsConfirmPopupShow(false));
    dispatch(panoramaActions.processPanorama(navigate));
  };

  const regenPanorama = () => {
    if (!canRegen) return;
    dispatch(panoramaActions.setIsRegenPopupShow(false));
    dispatch(panoramaActions.regenPanorama());
  };

  const clearAll = () => {
    dispatch(panoramaActions.clearData());
  };

  // 実行回数表示用
  const consumedRatio = isLimitInfinity ? '0%' : `${(creditConsumed / creditLimit) * 100}%`;
  const fileSizeMB = Math.floor(PanoramaMaxFileSize / (1024 * 1024));

  return (
    <>
      {/* パノラマステージング実行確認モーダル */}
      <ConfirmModal
        show={isConfirmPopupShow}
        onClose={closeConfirmPopup}
        onConfirm={processPanorama}
        icon={<ConfirmGenerateAfterPhotoIcon />}
        title={`パノラマステージングに${unprocessedImageCount}回分のクレジットを使用します。よろしいですか？`}
        confirmText="実行"
      >
        <div className="mt-2" />
        <div className="flex justify-between mb-1 text-gray-500">
          <span className="text-base font-normal">実行回数</span>
          <span className="text-sm font-semibold text-gray-900">
            {isLimitInfinity
              ? `実行回数 ${creditConsumed}回/上限なし(アカウントのクレジット上限)`
              : `残り${creditLimit - creditConsumed}回/${creditLimit}回`
            }
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className="bg-orange-500 h-2.5 rounded-full"
            style={{ width: consumedRatio }}
          />
        </div>
      </ConfirmModal>

      {/* 再生成確認モーダル */}
      <ConfirmModal
        show={isRegenPopupShow}
        onClose={closeRegenPopup}
        onConfirm={regenPanorama}
        icon={<ConfirmGenerateAfterPhotoIcon />}
        title="再生成すると新たに3枚の画像が追加されます。よろしいでしょうか。再生成では実行回数は消費されません。"
        confirmText="再生成"
      />

      {/* ファイルサイズエラーモーダル */}
      <AlertModal
        show={isPhotoTooBigPopupShow}
        onClose={closePhotoTooBigPopup}
        title={`${fileSizeMB}MB以上のサイズの画像はアップロードできません。`}
      />

      {/* アスペクト比エラーモーダル */}
      <AlertModal
        show={isAspectRatioErrorShow}
        onClose={closeAspectRatioError}
        title="パノラマ画像はアスペクト比2:1の画像をアップロードしてください。（例: 4000x2000, 6000x3000 など）"
      />

      {/* 全てクリア確認モーダル */}
      <ConfirmModal
        show={isClearAllPopupShow}
        onClose={closeClearAllPopup}
        onConfirm={clearAll}
        icon={<ConfirmGenerateAfterPhotoIcon />}
        title="初期画面に戻るため、生成した画像がダウンロードできませんがよろしいでしょうか？"
      />
    </>
  );
};

export default connect(mapStateToProps)(PanoramaModals);
